import React from 'react';
import { Calendar, Target, Zap, Star } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const JourneySection = () => {
  const { t } = useTranslation();

  const journeySteps = [
    {
      icon: Calendar,
      number: "1",
      title: t("journey.steps.1.title"),
      subtitle: t("journey.steps.1.subtitle"),
      description: t("journey.steps.1.description"),
      color: "warm-sage",
      duration: t("journey.steps.1.duration")
    },
    {
      icon: Target,
      number: "2",
      title: t("journey.steps.2.title"),
      subtitle: t("journey.steps.2.subtitle"),
      description: t("journey.steps.2.description"),
      color: "soft-taupe",
      duration: t("journey.steps.2.duration")
    },
    {
      icon: Zap,
      number: "3",
      title: t("journey.steps.3.title"),
      subtitle: t("journey.steps.3.subtitle"),
      description: t("journey.steps.3.description"),
      color: "rich-chocolate",
      // fix: step 3 should read its own duration
      duration: t("journey.steps.3.duration")
    },
    {
      icon: Star,
      number: "4",
      title: t("journey.steps.4.title"),
      subtitle: t("journey.steps.4.subtitle"),
      description: t("journey.steps.4.description"),
      color: "warm-sage",
      duration: t("journey.steps.4.duration")
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-cream-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header—désormais 100% i18n */}
        <div className="text-center mb-16 md:mb-24 max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-4xl font-semibold text-charcoal mb-4 tracking-tight">
            {t("journey.title")} : {t("journey.headerSuffix")}
          </h2>
          <p className="text-lg text-warm-gray font-light">
            {t("journey.headerLead")}
          </p>
          <div className="w-16 h-0.5 bg-warm-sage mx-auto mt-4 rounded-full" />
        </div>

        {/* Steps */}
        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-soft-gray z-0 -translate-y-1/2">
            <div className="h-full bg-gradient-to-r from-warm-sage via-soft-taupe to-rich-chocolate rounded-full" />
          </div>

          <div className="grid lg:grid-cols-4 gap-10 relative z-10">
            {journeySteps.map((step, index) => {
              const IconComponent = step.icon;
              const offsetClass = index % 2 === 1 ? 'lg:translate-y-10' : '';

              return (
                <Card
                  key={index}
                  className={`p-6 bg-white border-0 shadow-sm hover:shadow-lg transition-all duration-500 group relative flex flex-col items-start ${offsetClass} lg:items-center text-left lg:text-center`}
                >
                  <div className={`relative mb-4 lg:mb-6`}>
                    <div className={`bg-${step.color}/15 p-3 rounded-full w-14 h-14 flex items-center justify-center border-2 border-${step.color} group-hover:scale-105 transition-transform duration-300`}>
                      <IconComponent className={`h-6 w-6 text-${step.color}`} />
                    </div>
                    <div className={`absolute -top-1 -right-1 bg-${step.color} text-white w-5 h-5 rounded-full flex items-center justify-center font-bold text-xs shadow-md`}>
                      {step.number}
                    </div>
                  </div>

                  <div className="flex-1 w-full">
                    <h3 className="text-lg font-semibold text-charcoal mb-1 tracking-tight">
                      {step.title}
                    </h3>
                    <p className={`text-xs text-${step.color} font-medium mb-3 uppercase tracking-widest`}>
                      {step.duration}
                    </p>
                    <p className="text-sm text-warm-gray font-medium mb-3">
                      {step.subtitle}
                    </p>
                    <p className="text-charcoal leading-snug text-sm">
                      {step.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 text-center">
          <div className="bg-soft-gray/30 p-8 md:p-12 rounded-xl max-w-4xl mx-auto border border-soft-gray">
            <h3 className="text-xl sm:text-2xl font-semibold text-charcoal mb-4 tracking-tight">
              {t("journey.ctaTitle")}
            </h3>
            <p className="text-warm-gray mb-8">
              {t("journey.ctaText")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-warm-sage hover:bg-warm-sage/80 text-cream-white font-medium px-8 py-3 rounded-full transition-all duration-300 shadow-md"
              >
                {t("journey.ctaPrimary")}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => document.getElementById('apropos')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-warm-sage text-warm-sage hover:bg-warm-sage hover:text-white font-medium px-8 py-3 rounded-full transition-all duration-300"
              >
                {t("journey.ctaSecondary")}
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default JourneySection;
